import type { AgentsSummaryResponse } from '@kanbu/schema/contracts';
import { Agent } from '@kanbu/schema/enums';
import { Trans } from '@lingui/react/macro';
import { ChevronRightIcon } from 'lucide-react';
import { motion } from 'motion/react';
import { useImperativeHandle, useState, type ReactNode } from 'react';

import { FramerAnimations } from '@/components/framer-animations';
import { cn } from '@/lib';
import { Button } from '@/ui/button';

import { AccordionIdentifies } from './accordion-identifiers';
import { KnownFeedsAccordion } from './known-feeds-accordion';
import { KnownSourcesAccordion } from './known-sources-accordion';

export interface AgentInfoSectionRef {
  openSourcesAccordion: () => void;
  openFeedsAccordion: () => void;
}

export interface AgentInfoSectionProps {
  summary: AgentsSummaryResponse | undefined;
  className?: string;
  title?: ReactNode;
  chatId: string;
}

export function AgentInfoSection({
  summary,
  className,
  ref,
  title,
  chatId,
}: AgentInfoSectionProps & { ref?: React.Ref<AgentInfoSectionRef> }) {
  const [sourcesAccordionValue, setSourcesAccordionValue] = useState<string[]>(
    [],
  );
  const [feedsAccordionValue, setFeedsAccordionValue] = useState<string[]>([]);

  useImperativeHandle(
    ref,
    () =>
      ({
        openSourcesAccordion: () =>
          setSourcesAccordionValue([AccordionIdentifies.Sources]),
        openFeedsAccordion: () =>
          setFeedsAccordionValue([AccordionIdentifies.Feeds]),
      }) satisfies AgentInfoSectionRef,
  );

  return (
    <motion.div
      className={cn('flex flex-col mt-8', className)}
      variants={FramerAnimations.StaggerChildren}
      initial='initial'
      animate='animate'
    >
      {title && (
        <motion.h2
          className='ml-4 mb-2 font-medium'
          variants={FramerAnimations.FadeInFromTop}
        >
          {title}
        </motion.h2>
      )}

      <motion.div variants={FramerAnimations.FadeInFromTop}>
        {summary?.agent.agent === Agent.ShopAssistant && (
          <KnownFeedsAccordion
            className='mb-2'
            summary={summary}
            chatId={chatId}
            onValueChange={value => setFeedsAccordionValue(value)}
            value={feedsAccordionValue}
          />
        )}
      </motion.div>

      <motion.div variants={FramerAnimations.FadeInFromTop}>
        <KnownSourcesAccordion
          summary={summary}
          chatId={chatId}
          onValueChange={value => setSourcesAccordionValue(value)}
          value={sourcesAccordionValue}
        />
      </motion.div>

      <motion.div variants={FramerAnimations.FadeInFromTop}>
        <Button
          data-cta='playground-agent-info-section-go-to-kanbu-button'
          variant='link'
          asChild
          className='text-foreground'
        >
          <a href='https://kanbu.ai' target='_blank' rel='noreferrer'>
            <Trans>Go to Kanbu.ai website</Trans>{' '}
            <ChevronRightIcon className='size-4' />
          </a>
        </Button>
      </motion.div>
    </motion.div>
  );
}
